
import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Alert,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import axios from 'axios';

function ConfigStatus() {
  const [configStatus, setConfigStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchConfigStatus();
  }, []);

  const fetchConfigStatus = async () => {
    try {
      const response = await axios.get('/api/config/status');
      setConfigStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch config status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography>Loading configuration status...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (!configStatus) {
    return (
      <Alert severity="error">
        Failed to load configuration status. Please check if the backend is running.
      </Alert>
    );
  }

  const { is_ready, api_keys = {}, missing_keys = [], config = {} } = configStatus;

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" color="primary">
            Configuration Status
          </Typography>
          <Chip
            icon={is_ready ? <CheckIcon /> : <ErrorIcon />}
            label={is_ready ? "Ready" : "Not Ready"}
            color={is_ready ? "success" : "error"}
            variant="outlined"
          />
        </Box>

        {!is_ready && missing_keys && missing_keys.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Missing required API keys: {missing_keys.join(', ')}
            <br />
            Please set your API keys in environment variables or .env file.
          </Alert>
        )}

        <Button
          onClick={() => setExpanded(!expanded)}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          size="small"
        >
          {expanded ? 'Hide Details' : 'Show Details'}
        </Button>

        <Collapse in={expanded}>
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              API Keys Status:
            </Typography>
            <List dense>
              {Object.entries(api_keys).map(([key, valid]) => (
                <ListItem key={key}>
                  <ListItemIcon>
                    {valid ? (
                      <CheckIcon color="success" />
                    ) : (
                      <ErrorIcon color="error" />
                    )}
                  </ListItemIcon>
                  <ListItemText 
                    primary={key.toUpperCase()} 
                    secondary={valid ? 'Configured' : 'Missing'} 
                  />
                </ListItem>
              ))}
            </List>

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Current Configuration:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="LLM Provider" 
                  secondary={config.llm_provider || 'N/A'} 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Deep Thinking Model" 
                  secondary={config.deep_think_llm || 'N/A'} 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Quick Thinking Model" 
                  secondary={config.quick_think_llm || 'N/A'} 
                />
              </ListItem>
            </List>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

export default ConfigStatus;
